import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { MagnifyingGlass, X, Fire, FilmSlate, MusicNotes, Television } from "@phosphor-icons/react";
import { VideoCard } from "@/components/VideoCard";
import { fetchVideos } from "@/lib/api";

const PILLS = [
  { label: "2026 Releases", icon: Fire, year: 2026 },
  { label: "Horror", icon: FilmSlate, term: "horror" },
  { label: "Music Videos", icon: MusicNotes, term: "music" },
  { label: "Cult Classics", icon: Television, term: "classic" },
];

export const QuickSearchModal = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState("");
  const [year, setYear] = useState("");
  const [debounced, setDebounced] = useState("");
  const inputRef = useRef(null);

  // Open on "/" key (unless already typing) or via header event.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "/" ) {
        const tag = document.activeElement?.tagName;
        if (tag === "INPUT" || tag === "TEXTAREA") return;
        e.preventDefault();
        setOpen(true);
      }
    };
    const onOpen = () => setOpen(true);
    window.addEventListener("keydown", onKey);
    window.addEventListener("open-quick-search", onOpen);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("open-quick-search", onOpen);
    };
  }, []);

  // Debounce the term.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(term.trim()), 300);
    return () => clearTimeout(t);
  }, [term]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 60);
    else {
      setTerm("");
      setYear("");
      setDebounced("");
    }
  }, [open]);

  // Escape closes (capture phase so it doesn't hit the TV spatial-nav back handler).
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopImmediatePropagation();
        setOpen(false);
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [open]);

  const active = debounced.length >= 2 || !!year;
  const { data, isFetching } = useQuery({
    queryKey: ["quick-search", debounced, year],
    queryFn: () => fetchVideos({ page: 1, limit: 12, search: debounced, year: year || "" }),
    enabled: open && active,
  });
  const results = data?.items || [];

  if (!open) return null;

  return (
    <div
      data-testid="quick-search-modal"
      className="fixed inset-0 z-[70] bg-[#050505]/95 backdrop-blur-xl overflow-y-auto"
    >
      <div className="mx-auto max-w-5xl px-5 sm:px-8 py-10">
        <div className="flex items-center gap-4">
          <div className="relative flex-1">
            <MagnifyingGlass size={22} className="absolute left-4 top-1/2 -translate-y-1/2 text-amber" />
            <input
              ref={inputRef}
              data-testid="quick-search-input"
              value={term}
              onChange={(e) => {
                setTerm(e.target.value);
                setYear("");
              }}
              placeholder="Search films, keywords, moods…"
              className="w-full bg-[#111] border border-white/15 pl-12 pr-4 py-4 text-lg text-white placeholder-white/40 focus:outline-none focus:border-amber"
            />
          </div>
          <button
            data-testid="quick-search-close"
            onClick={() => setOpen(false)}
            className="flex items-center gap-2 border border-white/15 px-4 py-4 text-white/70 hover:border-crimson hover:text-crimson transition-colors duration-300"
          >
            <X size={20} weight="bold" />
          </button>
        </div>

        <div className="mt-5 flex flex-wrap gap-3">
          {PILLS.map((p) => {
            const Icon = p.icon;
            return (
              <button
                key={p.label}
                data-testid={`quick-pill-${p.label.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => {
                  if (p.year) {
                    setYear(String(p.year));
                    setTerm("");
                    setDebounced("");
                  } else {
                    setTerm(p.term);
                  }
                }}
                className="flex items-center gap-2 border border-white/15 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.15em] text-white/70 hover:border-amber hover:text-amber transition-colors duration-300"
              >
                <Icon size={14} weight="fill" /> {p.label}
              </button>
            );
          })}
        </div>

        <div className="mt-8">
          {!active ? (
            <p className="text-center font-mono text-xs uppercase tracking-[0.2em] text-white/30 py-16">
              Type to search or pick a shortcut above
            </p>
          ) : isFetching && results.length === 0 ? (
            <p className="text-center font-mono text-xs uppercase tracking-[0.2em] text-white/30 py-16">Searching…</p>
          ) : results.length === 0 ? (
            <p data-testid="quick-search-empty" className="text-center font-mono text-xs uppercase tracking-[0.2em] text-white/30 py-16">
              No films found
            </p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5" data-testid="quick-search-results">
              {results.map((v, i) => (
                <div key={v.video_id} onClick={() => setOpen(false)}>
                  <VideoCard video={v} index={i} />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
